import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    bannerUrl: String,
    userPushEnabled: { type: Boolean, default: true }
  }

  static targets = ["banner", "status", "toggle", "knob", "error"]

  connect() {
    this.ensureDebugFlag()
    this.registerServiceWorker().then(() => {
      this.updateStatus()
      this.maybeShowBanner()
      if (this.userPushEnabledValue) {
        this.syncSubscription()
      }
    })
  }

  ensureDebugFlag() {
    if (window.location.search.includes("push_debug=1")) {
      sessionStorage.setItem("push:debug", "true")
    }
  }

  isPushDebugEnabled() {
    return sessionStorage.getItem("push:debug") === "true"
  }

  userErrorMessage(error) {
    if (error && error.name === "AbortError") {
      return "Не удалось включить: сбой push-сервиса устройства. Попробуйте позже или перезагрузите телефон."
    }
    return "Не удалось изменить настройки уведомлений. Попробуйте ещё раз."
  }

  async registerServiceWorker() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      this.unsupported = true
      return
    }

    try {
      this.registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" })

      if (!this.registration.active) {
        console.warn("[push] SW registered but not active, attempting self-heal")
        await this.registration.unregister()
        this.registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
        console.warn("[push] SW self-healed:", this.registration.active ? "active" : "still inactive")
      }
    } catch (error) {
      this.pushDebug("register", error)
      this.unsupported = true
    }
  }

  async updateStatus() {
    if (!this.hasStatusTarget) return

    if (this.unsupported) {
      this.statusTarget.textContent = "Браузер не поддерживает пуш-уведомления"
      this.setToggle(false, true)
      return
    }

    const permission = Notification.permission

    if (this.hasBannerTarget && permission !== "default") {
      this.bannerTarget.classList.add("hidden")
    }

    if (permission === "denied") {
      this.statusTarget.textContent = "Уведомления заблокированы в браузере/системе. Разрешите в настройках сайта (замок в адресной строке) и в настройках Android для Chrome."
      this.setToggle(false, true)
      return
    }

    const existing = await this.getExistingSubscription()

    if (permission === "granted" && existing) {
      this.statusTarget.textContent = "Пуш-уведомления включены"
      this.setToggle(true)
    } else if (permission === "granted" && !existing) {
      this.statusTarget.textContent = "Разрешение получено, но подписка отсутствует. Нажмите, чтобы восстановить."
      this.setToggle(false)
    } else {
      this.statusTarget.textContent = "Нажмите, чтобы разрешить уведомления"
      this.setToggle(false)
    }
  }

  async syncSubscription() {
    if (!this.registration) return
    if (sessionStorage.getItem("push:synced") === "true") return

    const permission = Notification.permission
    if (permission === "denied") {
      sessionStorage.setItem("push:synced", "true")
      return
    }

    try {
      const subscription = await this.getExistingSubscription()

      if (subscription) {
        try {
          await this.saveSubscription(subscription)
        } catch (error) {
          this.pushDebug("post", error)
        }
      } else if (permission === "granted") {
        let newSubscription
        try {
          newSubscription = await this.subscribePush()
        } catch (error) {
          this.pushDebug("subscribe", error)
          return
        }

        try {
          await this.saveSubscription(newSubscription)
        } catch (error) {
          this.pushDebug("post", error)
          return
        }
      }
    } catch (error) {
      this.pushDebug("sync", error)
    } finally {
      sessionStorage.setItem("push:synced", "true")
    }
  }

  setToggle(enabled, disabled = false) {
    if (!this.hasToggleTarget || !this.hasKnobTarget) return

    this.toggleTarget.setAttribute("aria-checked", enabled.toString())
    this.toggleTarget.setAttribute("aria-label", enabled ? "Выключить пуш-уведомления" : "Включить пуш-уведомления")
    this.toggleTarget.disabled = disabled

    if (enabled) {
      this.toggleTarget.classList.remove("bg-gray-300")
      this.toggleTarget.classList.add("bg-brand")
      this.knobTarget.classList.remove("translate-x-0.5")
      this.knobTarget.classList.add("translate-x-5")
    } else {
      this.toggleTarget.classList.add("bg-gray-300")
      this.toggleTarget.classList.remove("bg-brand")
      this.knobTarget.classList.add("translate-x-0.5")
      this.knobTarget.classList.remove("translate-x-5")
    }
  }

  setLoading(loading) {
    if (!this.hasToggleTarget || !this.hasKnobTarget) return

    this.toggleTarget.disabled = loading
    this.knobTarget.classList.toggle("hidden", loading)

    const existingSpinner = this.toggleTarget.querySelector("[data-push-notifications-target='spinner']")
    if (loading) {
      if (!existingSpinner) {
        const spinner = document.createElement("span")
        spinner.setAttribute("data-push-notifications-target", "spinner")
        spinner.className = "absolute inset-0 flex items-center justify-center"
        spinner.innerHTML = `
          <svg class="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        `
        this.toggleTarget.appendChild(spinner)
      }
    } else if (existingSpinner) {
      existingSpinner.remove()
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  pushDebug(step, error) {
    const name = error && error.name ? error.name : "unknown"
    const message = error && error.message ? error.message : ""
    const debugText = `PUSH-DEBUG: шаг=${step} name=${name} msg=${message}`
    console.error("[push]", debugText, error)

    if (this.isPushDebugEnabled()) {
      this.showError(debugText)
    } else {
      this.showError(this.userErrorMessage(error))
    }
  }

  async maybeShowBanner() {
    if (!this.hasBannerTarget) return
    if (!this.registration) return
    if (!this.userPushEnabledValue) return
    if (sessionStorage.getItem("push:banner:dismissed") === "true") return

    const permission = Notification.permission
    if (permission === "granted" || permission === "denied") return

    const existing = await this.getExistingSubscription()
    if (existing) return

    this.bannerTarget.classList.remove("hidden")
  }

  dismissBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden")
    }
    sessionStorage.setItem("push:banner:dismissed", "true")
  }

  async toggle() {
    if (!this.hasToggleTarget) return

    this.clearError()
    this.setLoading(true)

    try {
      const existing = await this.getExistingSubscription()
      if (existing) {
        await this.disable()
        this.userPushEnabledValue = false
      } else {
        const enabled = await this.enable()
        if (enabled) this.userPushEnabledValue = true
      }
      await this.updateStatus()
    } catch (error) {
      this.pushDebug("toggle", error)
    } finally {
      this.setLoading(false)
    }
  }

  async enable() {
    if (!this.registration) {
      this.showError("Браузер не поддерживает пуш-уведомления")
      return false
    }

    let permission
    try {
      permission = await Notification.requestPermission()
    } catch (error) {
      this.pushDebug("requestPermission", error)
      return false
    }

    if (permission === "denied") {
      this.showError("Уведомления заблокированы в настройках браузера")
      return false
    }
    if (permission !== "granted") {
      return false
    }

    let subscription
    try {
      subscription = await this.subscribePush()
    } catch (error) {
      this.pushDebug("subscribe", error)
      return false
    }

    try {
      await this.saveSubscription(subscription)
    } catch (error) {
      this.pushDebug("post", error)
      return false
    }

    this.dismissBanner()
    return true
  }

  async subscribePush() {
    const publicKey = this.publicKey()
    if (!publicKey) throw new Error("VAPID public key missing")

    const options = {
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(publicKey)
    }

    try {
      return await this.registration.pushManager.subscribe(options)
    } catch (error) {
      if (error.name === "InvalidStateError") {
        console.warn("[push] subscribe InvalidStateError, attempting self-heal")
        const existing = await this.registration.pushManager.getSubscription()
        if (existing) {
          await existing.unsubscribe()
        }
        const retried = await this.registration.pushManager.subscribe(options)
        console.warn("[push] subscribe self-healed")
        return retried
      }
      throw error
    }
  }

  async getExistingSubscription() {
    if (!this.registration) return null
    return this.registration.pushManager.getSubscription()
  }

  async saveSubscription(subscription) {
    const json = subscription.toJSON()

    const response = await fetch("/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({
        push_subscription: {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          user_agent: navigator.userAgent
        }
      })
    })

    if (!response.ok) {
      throw new Error(`Failed to save subscription: ${response.status}`)
    }
  }

  async disable() {
    const subscription = await this.getExistingSubscription()
    if (subscription) {
      await this.deleteSubscription(subscription)
      await subscription.unsubscribe()
    } else {
      await this.deleteSubscription(null)
    }
  }

  async deleteSubscription(subscription) {
    const body = subscription ? { endpoint: subscription.toJSON().endpoint } : {}

    const response = await fetch("/push_subscriptions", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(body)
    })

    if (!response.ok) {
      throw new Error(`Failed to delete subscription: ${response.status}`)
    }
  }

  publicKey() {
    const meta = document.querySelector('meta[name="vapid-public-key"]')
    return meta ? meta.content : null
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/\-/g, "+").replace(/_/g, "/")
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
